import { useCallback, useRef, useState } from 'react';
import { Device } from 'mediasoup-client';
import { SERVER } from './api';
import { rtcLog } from './components/CallPanel';

const request = (socket, event, payload) => new Promise((resolve, reject) => {
  socket.emit(event, payload, response => response?.ok ? resolve(response) : reject(new Error(response?.error || `${event} failed`)));
});

export default function useSfuCall(socket) {
  const [peers, setPeers] = useState({});
  const [localStream, setLocalStream] = useState(null);
  const [screenStream, setScreenStream] = useState(null);
  const [muted, setMuted] = useState(false);
  const [camOff, setCamOff] = useState(false);
  const [sharing, setSharing] = useState(false);
  const state = useRef({ channelId: null, device: null, send: null, recv: null, producers: new Map(), consumers: new Map(), local: null });

  const consume = useCallback(async descriptor => {
    const s = state.current;
    if (!s.device || !s.recv || s.consumers.has(descriptor.producerId)) return;
    const response = await request(socket, 'sfu_consume', { channelId: s.channelId, transportId: s.recv.id, producerId: descriptor.producerId, rtpCapabilities: s.device.rtpCapabilities });
    const consumer = await s.recv.consume(response.consumer);
    s.consumers.set(descriptor.producerId, consumer);
    const stream = new MediaStream([consumer.track]);
    setPeers(current => {
      const prior = current[descriptor.peerId]?.stream;
      const tracks = prior ? [...prior.getTracks().filter(track => track.kind !== consumer.track.kind), consumer.track] : [consumer.track];
      return { ...current, [descriptor.peerId]: { username: descriptor.username || 'Participant', userId: descriptor.userId, stream: new MediaStream(tracks), topology: 'sfu' } };
    });
    await request(socket, 'sfu_resume_consumer', { channelId: s.channelId, consumerId: consumer.id });
  }, [socket]);

  const join = useCallback(async (channelId, { video = false } = {}) => {
    if (!socket) throw new Error('Socket is not connected');
    const token = localStorage.getItem('tavern_token');
    const topology = await fetch(`${SERVER}/api/upgrade/calls/topology/${encodeURIComponent(channelId)}`, { headers: { Authorization: `Bearer ${token}` } }).then(r => r.json());
    if (topology.topology !== 'sfu') throw new Error('SFU was not selected for this room');
    let media, send, recv;
    try {
      media = await navigator.mediaDevices.getUserMedia({ audio: true, video: video ? { width: { ideal: 1280 }, height: { ideal: 720 } } : false });
      const capabilities = await request(socket, 'sfu_get_capabilities', { channelId });
      const device = new Device();
      await device.load({ routerRtpCapabilities: capabilities.routerRtpCapabilities });
      const sendInfo = await request(socket, 'sfu_create_transport', { channelId, direction: 'send' });
      const recvInfo = await request(socket, 'sfu_create_transport', { channelId, direction: 'recv' });
      send = device.createSendTransport(sendInfo.transport);
      recv = device.createRecvTransport(recvInfo.transport);
    send.on('connect', ({ dtlsParameters }, ok, fail) => request(socket, 'sfu_connect_transport', { channelId, transportId: send.id, dtlsParameters }).then(ok, fail));
    recv.on('connect', ({ dtlsParameters }, ok, fail) => request(socket, 'sfu_connect_transport', { channelId, transportId: recv.id, dtlsParameters }).then(ok, fail));
    send.on('produce', ({ kind, rtpParameters, appData }, ok, fail) => request(socket, 'sfu_produce', { channelId, transportId: send.id, kind, rtpParameters, appData }).then(({ id }) => ok({ id }), fail));
      state.current = { channelId, device, send, recv, producers: new Map(), consumers: new Map(), local: media };
      setLocalStream(media); setCamOff(!video); setPeers({});
      for (const track of media.getTracks()) state.current.producers.set(track.kind, await send.produce({ track, encodings: track.kind === 'video' ? [
        { rid: 'q', scaleResolutionDownBy: 4, maxBitrate: 300000 },
        { rid: 'h', scaleResolutionDownBy: 2, maxBitrate: 900000 },
        { rid: 'f', scaleResolutionDownBy: 1, maxBitrate: 2200000 },
      ] : undefined, appData: { source: track.kind } }));
      const list = await request(socket, 'sfu_list_producers', { channelId });
      for (const producer of list.producers) await consume(producer);
      socket.off('sfu_new_producer');
      socket.on('sfu_new_producer', descriptor => descriptor.channelId === channelId && consume(descriptor).catch(error => rtcLog.push(`SFU consume failed: ${error.message}`, 'err')));
      rtcLog.push(`SFU joined on mediasoup (${topology.participant_count} participants)`, 'ok');
    } catch (error) {
      send?.close(); recv?.close(); media?.getTracks().forEach(track => track.stop());
      throw error;
    }
  }, [socket, consume]);

  const leave = useCallback(() => {
    const s = state.current;
    if (s.channelId) socket?.emit('voice_leave', { channelId: s.channelId });
    for (const consumer of s.consumers.values()) consumer.close();
    for (const producer of s.producers.values()) producer.close();
    s.send?.close(); s.recv?.close(); s.local?.getTracks().forEach(track => track.stop());
    screenStream?.getTracks().forEach(track => track.stop());
    state.current = { channelId: null, device: null, send: null, recv: null, producers: new Map(), consumers: new Map(), local: null };
    socket?.off('sfu_new_producer');
    setPeers({}); setLocalStream(null); setScreenStream(null); setMuted(false); setCamOff(false); setSharing(false);
  }, [socket, screenStream]);

  const toggleMute = useCallback(() => { const track = state.current.local?.getAudioTracks()[0]; if (track) { track.enabled = !track.enabled; setMuted(!track.enabled); } }, []);
  const toggleCamera = useCallback(() => { const track = state.current.local?.getVideoTracks()[0]; if (track) { track.enabled = !track.enabled; setCamOff(!track.enabled); } }, []);
  const toggleScreenShare = useCallback(async () => {
    const s = state.current;
    if (!s.send) return;
    if (sharing) {
      screenStream?.getTracks().forEach(track => track.stop()); setScreenStream(null); setSharing(false);
      const camera = s.local?.getVideoTracks()[0]; const producer = s.producers.get('video'); if (camera && producer) await producer.replaceTrack({ track: camera });
    } else {
      const display = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      const track = display.getVideoTracks()[0]; const producer = s.producers.get('video');
      if (producer) await producer.replaceTrack({ track }); else s.producers.set('video', await s.send.produce({ track, encodings: [{ maxBitrate: 2200000 }], appData: { source: 'screen' } }));
      track.onended = () => setSharing(false); setScreenStream(display); setSharing(true);
    }
  }, [sharing, screenStream]);

  const setVideoBitrate = useCallback(async maxKbps => {
    const producer = state.current.producers.get('video');
    if (producer) try { await producer.setMaxSpatialLayer(maxKbps >= 1500 ? 2 : maxKbps >= 600 ? 1 : 0); } catch {}
  }, []);

  return { peers, localStream, screenStream, muted, camOff, sharing, join, leave, toggleMute, toggleCamera, toggleScreenShare, setVideoBitrate, topology: 'sfu' };
}
